<template>
  <div>
    <v-card>
      <v-card-title class="pb-0 pt-5">
        <v-row data-testid="warehouse-reporting-filters">
          <v-col cols="12" lg="3">
            <warehouse-multi-select
              :is-flex="false"
              hide-icon
              auto-select-all
              ref="reportingWarehouseSelect"
              dense
              placeholder="Warehouses"
              label="Warehouses"
              :joins="['docks||id,name']"
              :select-fields="['id,name,facilityNumber']"
              @input="
                ws =>
                  persistLastSelectedData(
                    'warehouseIds',
                    ws?.map(w => w.id)
                  )
              "
              :auto-select-by-ids="getLastSelectedWarehouseIds()"
              v-model="filters.selectedWarehouses"
              show-store-number></warehouse-multi-select>
          </v-col>
          <v-col cols="12" lg="2">
            <v-select
              hide-details
              dense
              :items="groupByOptions"
              v-model="filters.groupBy"
              label="Group By"></v-select>
          </v-col>
          <v-col cols="12" class="d-flex align-center" lg="4">
            <date-range
              v-if="dateRangesReady"
              height="26px"
              :dense="true"
              :initial-range="dateRange"
              clearable
              :disabled="filters.showAllTime"
              label="Date Range"
              v-model="dateRange"></date-range>
          </v-col>
          <v-spacer></v-spacer>
          <primary-button
            class="mt-1"
            :loading="buildingExcelData"
            before-icon="microsoft-excel"
            @click="exportExcel"
            >Export to Excel</primary-button
          >
        </v-row>
      </v-card-title>
      <div class="d-flex justify-center mt-5">
        <v-alert outlined type="info" dense border="left" class="d-inline-flex">
          The warehouse insights page only shows data for <strong>completed</strong> appointments,
          it may not reflect the total appointments for each warehouse.
        </v-alert>
      </div>
      <v-data-table
        :headers="headers"
        :items="metrics"
        data-testid="warehouse-metrics-table"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="$data.$globalLoading">
        <template v-slot:item.dwellTimeAvg="{ item }">
          {{ formatDataPoint(item.dwellTimeAvg, ' min') }}
        </template>

        <template v-slot:item.waitTimeAvg="{ item }">
          {{ formatDataPoint(item.waitTimeAvg, ' min') }}
        </template>

        <template v-slot:item.processingTimeAvg="{ item }">
          {{ formatDataPoint(item.processingTimeAvg, ' min') }}
        </template>

        <template v-slot:item.onTimePct="{ item }">
          {{ formatDataPoint(item.onTimePct, '%') }}
        </template>

        <template v-slot:item.latePct="{ item }">
          {{ formatDataPoint(item.latePct, '%') }}
        </template>

        <template v-slot:item.cancelPct="{ item }">
          {{ formatDataPoint(item.cancelPct, '%') }}
        </template>

        <template v-slot:item.noShowPct="{ item }">
          {{ formatDataPoint(item.noShowPct, '%') }}
        </template>

        <template v-slot:item.rescheduledPct="{ item }">
          {{ formatDataPoint(item.rescheduledPct, '%') }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import dataListMixin from '@satellite/components/mixins/dataListMixin';
import { utils, writeFile } from 'xlsx';
import moment from 'moment-timezone';
import dateRangeFilterMixin from '@/modules/insights/components/mixins/dateRangeFilterMixin';
import persistFilterMixin from '@/modules/insights/components/mixins/persistFilterMixin';

/**
 * Warehouse Metrics Table
 * @displayName Warehouse Metrics Table
 */
export default {
  mixins: [dataListMixin, dateRangeFilterMixin, persistFilterMixin],
  computed: {
    groupByOptions() {
      return Object.values(this.novaCore.WarehouseMetricGroupOptions).map(opt => {
        return {
          text: this.novaCore.ucWords(opt),
          value: opt
        };
      });
    }
  },
  data() {
    return {
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: '# of Appts', value: 'totalAppointments', sortable: true },
        { text: 'Avg Dwell Time', value: 'dwellTimeAvg', sortable: true },
        { text: 'Avg Waiting Time', value: 'waitTimeAvg', sortable: true },
        { text: 'Avg Processing Time', value: 'processingTimeAvg', sortable: true },
        { text: 'Self Check-in %', value: 'selfCheckInPct', sortable: true },
        { text: 'On Time %', value: 'onTimePct', sortable: true },
        { text: 'Late %', value: 'latePct', sortable: true },
        { text: 'No Show %', value: 'noShowPct', sortable: true },
        { text: 'Cancelled %', value: 'cancelPct', sortable: true }
      ],
      metrics: [],
      warehouses: [],
      currentRequests: [],
      buildingExcelData: false,
      sortDesc: [true],
      useCurrentWeek: true,
      sortBy: ['totalAppointments'],
      filters: {
        selectedWarehouses: [],
        showAllTime: false,
        groupBy: this.novaCore.WarehouseMetricGroupOptions.WAREHOUSE
      }
    };
  },
  methods: {
    async getData() {
      if (!this.filters.selectedWarehouses?.length || !this.dateRange?.length) {
        return;
      }
      const data = {
        warehouseIds: this.filters.selectedWarehouses.map(wh => wh.id),
        groupBy: this.filters.groupBy
      };

      if (!this.filters.showAllTime) {
        data.fromDate = this.dateRange[0];
        data.toDate = this.dateRange[1];
      }
      const url = '/metrics/warehouse';
      const semaphoreKey = encodeURI(`${url}-${JSON.stringify(data)}`);

      if (this.currentRequests.includes(semaphoreKey)) {
        return;
      }
      this.currentRequests.push(semaphoreKey);

      axios
        .post(url, data)
        .then(response => {
          if (response?.data) {
            this.metrics = response.data.data;
          }
        })
        .finally(
          () => (this.currentRequests = this.currentRequests.filter(r => r !== semaphoreKey))
        );
    },
    getLastSelectedWarehouseIds() {
      const filterData = this.getPersistedFilterData();
      if (filterData?.warehouseIds) {
        return filterData.warehouseIds;
      }
    },
    formatDataPoint(value, suffix = '', prefix = '', placeholder = '----') {
      return value && !isNaN(value) ? `${prefix}${value}${suffix}` : placeholder;
    },
    excelFileName() {
      return `warehouse_insights_${moment().format(this.novaCore.DateTimeFormats.DateDashed)}.xlsx`;
    },
    exportExcel() {
      this.buildingExcelData = true;
      const data = [];

      this.metrics.forEach(metric => {
        const row = {
          Name: metric.name,
          '# of Appts': metric.totalAppointments,
          'Avg Dwell Time': this.formatDataPoint(metric.dwellTimeAvg, ' min'),
          'Avg Waiting Time': this.formatDataPoint(metric.waitTimeAvg, ' min'),
          'Avg Processing Time': this.formatDataPoint(metric.processingTimeAvg, ' min'),
          'Self Check-in %': this.formatDataPoint(metric.selfCheckInPct, '%'),
          'On Time %': this.formatDataPoint(metric.onTimePct, '%'),
          'Late %': this.formatDataPoint(metric.latePct, '%'),
          'No Show %': this.formatDataPoint(metric.noShowPct, '%'),
          'Cancelled %': this.formatDataPoint(metric.cancelPct, '%')
        };

        data.push(row);
      });

      const wb = utils.book_new();

      utils.book_append_sheet(wb, utils.json_to_sheet(data), 'Warehouses');

      writeFile(wb, this.excelFileName());
      this.trackExportMixpanelEvent();
      this.buildingExcelData = false;
    },
    trackExportMixpanelEvent() {
      this.mixpanel.track(this.mixpanel.events.MODULE.REPORTING.WAREHOUSE_REPORT_EXPORTED, {
        'Org Name': this.$org.name,
        'Org ID': this.$org.id,
        'Group Report By': this.filters.groupBy
      });
    }
  },
  watch: {
    dateRange() {
      this.debounceInput();
    },
    filters: {
      handler() {
        this.debounceInput();
      },
      deep: true
    }
  },
  beforeMount() {
    this.initDateRanges();
  }
};
</script>
